import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api';
import { formatCurrency } from '../../utils/formatCurrency';
import { isOperationalPaymentPlanMode } from '../../utils/paymentPlanDisplay';
import { resolvePayableInstallments } from '../../utils/paymentPlanPayables';
import '../../styles/payments.css';

/** Saldo pendiente de la cuota (core: balance.remain.total). */
function getInstallmentRemainTotal(installment) {
    const raw = installment?.balance?.remain?.total;
    if (raw === undefined || raw === null) return 0;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : 0;
}

function getInstallmentDueDate(installment) {
    const dueDate = installment?.dates?.due_date;
    if (!dueDate) return null;

    const dueDateStr = String(dueDate).split(/[\sT]/)[0];
    const [year, month, day] = dueDateStr.split('-').map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
}

function PaymentModal({
    isOpen,
    onClose,
    loan,
    installments,
    token,
    displayMode,
    paymentPlan = null,
}) {
    const [selectedInstallments, setSelectedInstallments] = useState([]);
    const [paymentMode, setPaymentMode] = useState('installments');
    const [customAmount, setCustomAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('mercadopago');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: select installments, 2: confirm payment
    const [error, setError] = useState(null);

    const isPaymentPlanMode = isOperationalPaymentPlanMode(displayMode) && Boolean(paymentPlan?.items?.length);

    const pendingInstallments = useMemo(
        () => resolvePayableInstallments({ displayMode, paymentPlan, installments }),
        [displayMode, paymentPlan, installments]
    );

    // Calculate total amount
    const calculateTotal = () => {
        return selectedInstallments.reduce((sum, instNumber) => {
            const inst = pendingInstallments.find(i => i.number === instNumber);
            return sum + getInstallmentRemainTotal(inst);
        }, 0);
    };

    const totalAmount = calculateTotal();
    const totalPendingAmount = pendingInstallments.reduce(
        (sum, inst) => sum + getInstallmentRemainTotal(inst),
        0
    );
    const currentMonthDueAmount = pendingInstallments.reduce((sum, inst) => {
        const dueDate = getInstallmentDueDate(inst);
        if (!dueDate) return sum;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const isOverdue = dueDate < today;
        const isDueThisMonth = (
            dueDate.getFullYear() === now.getFullYear()
            && dueDate.getMonth() === now.getMonth()
        );

        return isOverdue || isDueThisMonth
            ? sum + getInstallmentRemainTotal(inst)
            : sum;
    }, 0);

    const parseCurrencyInput = (value) => {
        const normalizedValue = String(value)
            .trim()
            .replace(/\s/g, '')
            .replace(/\$/g, '');

        if (!normalizedValue) return 0;

        const decimalValue = normalizedValue.includes(',')
            ? normalizedValue.replace(/\./g, '').replace(',', '.')
            : normalizedValue;
        const parsedValue = Number(decimalValue);

        return Number.isFinite(parsedValue) ? parsedValue : 0;
    };

    const customPaymentAmount = parseCurrencyInput(customAmount);
    const isCustomPayment = paymentMode === 'custom';
    const finalAmount = isCustomPayment ? customPaymentAmount : totalAmount; // No surcharge applied

    const customAmountError = (() => {
        if (!isCustomPayment || customAmount.trim() === '') return null;
        if (customPaymentAmount <= 0) return 'Ingresá un monto mayor a $0';
        if (customPaymentAmount > totalPendingAmount) return 'El monto no puede superar el saldo pendiente';
        return null;
    })();

    const canContinue = isCustomPayment
        ? customPaymentAmount > 0 && customPaymentAmount <= totalPendingAmount
        : selectedInstallments.length > 0;

    // Toggle installment selection
    const toggleInstallment = (instNumber) => {
        setSelectedInstallments(prev => {
            if (prev.includes(instNumber)) {
                return prev.filter(n => n !== instNumber);
            } else {
                return [...prev, instNumber].sort((a, b) => a - b);
            }
        });
    };

    // Select all installments
    const selectAllInstallments = () => {
        if (selectedInstallments.length === pendingInstallments.length) {
            setSelectedInstallments([]);
        } else {
            setSelectedInstallments(pendingInstallments.map(inst => inst.number));
        }
    };

    const selectPaymentMode = (mode) => {
        setPaymentMode(mode);
        setError(null);
    };

    const buildCustomInstallmentDetails = () => {
        let remainingAmount = customPaymentAmount;
        const details = [];

        pendingInstallments.forEach((inst) => {
            if (remainingAmount <= 0) return;

            const installmentAmount = getInstallmentRemainTotal(inst);
            const amountToApply = Math.min(installmentAmount, remainingAmount);

            if (amountToApply > 0) {
                details.push({
                    number: inst.number,
                    amount: Number(amountToApply.toFixed(2)),
                    period: inst.dates?.period?.label || null,
                    due_date: inst.dates?.due_date || null
                });
                remainingAmount = Number((remainingAmount - amountToApply).toFixed(2));
            }
        });

        return details;
    };

    // Handle payment
    const handlePayment = async () => {
        if (!canContinue) {
            setError(isCustomPayment ? 'Ingresá un monto válido para pagar' : 'Debes seleccionar al menos una cuota');
            return;
        }

        if (isCustomPayment && customAmountError) {
            setError(customAmountError);
            return;
        }

        if (pendingInstallments.length === 0) {
            setError('No hay saldo pendiente para pagar');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Build detailed installment data
            const installmentDetails = isCustomPayment
                ? buildCustomInstallmentDetails()
                : selectedInstallments.map(instNumber => {
                    const inst = pendingInstallments.find(i => i.number === instNumber);
                    return {
                        number: inst.number,
                        amount: getInstallmentRemainTotal(inst),
                        period: inst.dates?.period?.label || null,
                        due_date: inst.dates?.due_date || null
                    };
                });

            const paymentPayload = {
                installments: installmentDetails,
                payment_method: paymentMethod,
                amount: Number(finalAmount.toFixed(2)),
                loan_product: loan.product || 'Préstamo',
                loan_reference: loan.reference,
            };

            if (isPaymentPlanMode && paymentPlan?.uid) {
                paymentPayload.payment_plan_uid = paymentPlan.uid;
            }

            const response = await api.post(`/c/${token}/l/${loan.uid}/payment`, paymentPayload, {
                headers: { dni: localStorage.getItem(token + 'dni') }
            });

            if (response.data.payment_link) {
                // Redirect to MercadoPago
                window.location.href = response.data.payment_link;
            } else {
                setError('No se pudo generar el link de pago');
            }
        } catch (error) {
            console.error('Error creating payment:', error);
            setError('Hubo un error al procesar el pago. Por favor, intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedInstallments([]);
            setPaymentMode('installments');
            setCustomAmount('');
            setStep(1);
            setError(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="modal payment-modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header border-0">
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                            disabled={loading}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body px-4 pb-4">
                        {step === 1 ? (
                            // Step 1: Select installments
                            <>
                                <h5 className="mb-2 text-center">Seleccioná cómo querés pagar</h5>
                                <p className="text-muted text-center mb-4">
                                    Préstamo #{loan.reference}
                                    {isPaymentPlanMode && (
                                        <>
                                            <br />
                                            <small>Plan de pagos vigente</small>
                                        </>
                                    )}
                                </p>

                                {/* Payment mode */}
                                <div className="payment-mode-selector mb-4">
                                    <button
                                        type="button"
                                        className={`payment-mode-card ${paymentMode === 'installments' ? 'active' : ''}`}
                                        onClick={() => selectPaymentMode('installments')}
                                    >
                                        <span className="fw-semibold">Pagar cuotas</span>
                                        <small>
                                            {isPaymentPlanMode
                                                ? 'Elegí uno o más compromisos del plan'
                                                : 'Elegí una o más cuotas pendientes'}
                                        </small>
                                    </button>
                                    <button
                                        type="button"
                                        className={`payment-mode-card ${paymentMode === 'custom' ? 'active' : ''}`}
                                        onClick={() => selectPaymentMode('custom')}
                                    >
                                        <span className="fw-semibold">Ingresar monto</span>
                                        <small>Pagá un importe parcial o exacto</small>
                                    </button>
                                </div>

                                {isCustomPayment ? (
                                    <div className="custom-payment-card mb-4">
                                        <label htmlFor="customPaymentAmount" className="form-label fw-semibold">
                                            Monto a pagar
                                        </label>
                                        <div className="input-group input-group-lg">
                                            <span className="input-group-text">$</span>
                                            <input
                                                id="customPaymentAmount"
                                                type="text"
                                                inputMode="decimal"
                                                className={`form-control ${customAmountError ? 'is-invalid' : ''}`}
                                                value={customAmount}
                                                onChange={(event) => {
                                                    setCustomAmount(event.target.value);
                                                    setError(null);
                                                }}
                                                placeholder="Ej: 15000"
                                                aria-describedby={[
                                                    currentMonthDueAmount > 0 ? 'customPaymentHelp' : null,
                                                    customAmountError ? 'customPaymentError' : null
                                                ].filter(Boolean).join(' ') || undefined}
                                            />
                                            {customAmountError && (
                                                <div id="customPaymentError" className="invalid-feedback">
                                                    {customAmountError}
                                                </div>
                                            )}
                                        </div>
                                        {currentMonthDueAmount > 0 && (
                                            <small id="customPaymentHelp" className="text-muted d-block mt-2">
                                                Saldo vencido o a vencer este mes: {formatCurrency(currentMonthDueAmount)}
                                            </small>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {/* Select all toggle */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <span className="fw-semibold">Seleccionar todas</span>
                                            <div className="form-check form-switch">
                                                <input 
                                                    className="form-check-input" 
                                                    type="checkbox" 
                                                    id="selectAll"
                                                    checked={selectedInstallments.length === pendingInstallments.length}
                                                    onChange={selectAllInstallments}
                                                />
                                            </div>
                                        </div>

                                        {/* Divider */}
                                        <div className="border-top mb-3"></div>

                                        {/* Installments to pay */}
                                        <div className="mb-3">
                                            <small className="text-muted">
                                                {isPaymentPlanMode ? 'Compromisos del plan' : 'Cuotas a pagar'}
                                            </small>
                                        </div>

                                        <div className="installments-selection" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {pendingInstallments.length === 0 ? (
                                                <div className="text-center text-muted py-4">
                                                    <i className="bi bi-check-circle display-4"></i>
                                                    <p className="mt-2">
                                                        {isPaymentPlanMode
                                                            ? 'No hay compromisos pendientes'
                                                            : 'No hay cuotas pendientes'}
                                                    </p>
                                                </div>
                                            ) : (
                                                pendingInstallments.map((inst) => {
                                                    const isSelected = selectedInstallments.includes(inst.number);
                                                    return (
                                                        <div 
                                                            key={inst.number}
                                                            className={`card mb-3 cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                                                            onClick={() => toggleInstallment(inst.number)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="card-body p-3">
                                                                <div className="d-flex align-items-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input me-3"
                                                                        checked={isSelected}
                                                                        onChange={() => {}}
                                                                        style={{ 
                                                                            width: '24px', 
                                                                            height: '24px',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-0">Cuota {inst.number}</h6>
                                                                        <small className="text-muted">
                                                                            {inst.dates?.period?.label || new Date(inst.dates.due_date).toLocaleDateString('es-AR')}
                                                                        </small>
                                                                    </div>
                                                                    <div className="text-end">
                                                                        <h6 className="mb-0">{formatCurrency(getInstallmentRemainTotal(inst))}</h6>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>

                                        {/* Total */}
                                        {selectedInstallments.length > 0 && (
                                            <>
                                                <div className="border-top mt-4 mb-3"></div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="fw-semibold fs-5">Total a pagar</span>
                                                    <span className="fw-bold fs-5">{formatCurrency(totalAmount)}</span>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {isCustomPayment && customPaymentAmount > 0 && !customAmountError && (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold fs-5">Total a pagar</span>
                                        <span className="fw-bold fs-5">{formatCurrency(customPaymentAmount)}</span>
                                    </div>
                                )}

                                {/* Continue button */}
                                <button 
                                    className="btn btn-primary w-100 mt-4 py-3 rounded-pill"
                                    onClick={() => setStep(2)}
                                    disabled={!canContinue}
                                >
                                    Continuar
                                </button>
                            </>
                        ) : (
                            // Step 2: Confirm payment
                            <>
                                <h5 className="mb-2 text-center">¿Cómo te gustaría pagar?</h5>
                                <p className="text-muted text-center mb-4">
                                    Total a pagar: {formatCurrency(finalAmount)}
                                </p>

                                {/* Payment methods */}
                                <div className="mb-4">
                                    <small className="text-muted">Acreditación inmediata</small>
                                </div>

                                {/* MercadoPago option */}
                                <div 
                                    className={`card mb-3 cursor-pointer ${paymentMethod === 'mercadopago' ? 'border-primary' : ''}`}
                                    onClick={() => setPaymentMethod('mercadopago')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <img 
                                                src="/methods/mercadopago.png" 
                                                alt="MercadoPago" 
                                                style={{ width: '40px', height: '40px', marginRight: '12px' }}
                                            />
                                            <div className="flex-grow-1">
                                                <h6 className="mb-0">Mercado Pago</h6>
                                                <small className="text-muted">Dinero en cuenta o tarjetas.</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment summary */}
                                <div className="border-top mb-3"></div>
                                
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Préstamo #{loan.reference}</span>
                                    </div>
                                    {isCustomPayment ? (
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Monto ingresado</span>
                                            <span>{formatCurrency(finalAmount)}</span>
                                        </div>
                                    ) : (
                                        selectedInstallments.map(instNumber => {
                                            const inst = pendingInstallments.find(i => i.number === instNumber);
                                            return (
                                                <div key={instNumber} className="d-flex justify-content-between mb-2">
                                                    <span>Cuota {instNumber}</span>
                                                    <span>{formatCurrency(getInstallmentRemainTotal(inst))}</span>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div className="border-top mt-2 pt-2"></div>
                                    <div className="d-flex justify-content-between">
                                        <span className="fw-bold">Total a pagar</span>
                                        <span className="fw-bold fs-5">{formatCurrency(finalAmount)}</span>
                                    </div>
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Método de pago: Mercado Pago
                                    </small>
                                </div>

                                {/* Error message */}
                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        <i className="bi bi-exclamation-circle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="d-grid gap-2">
                                    <button 
                                        className="btn btn-primary py-3 rounded-pill"
                                        onClick={handlePayment}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Procesando...
                                            </>
                                        ) : (
                                            'Continuar'
                                        )}
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary py-2 rounded-pill"
                                        onClick={() => setStep(1)}
                                        disabled={loading}
                                    >
                                        Volver
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PaymentModal;
